import { datadogLogs } from "@datadog/browser-logs";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { queryClient } from "src/libs";
import { oktaAuthTokenDecoder } from "src/services/oktaAuth.tokenDecoder";
import { cookieStorage, getMyLoginFromAccessToken } from "src/utils";

/** Buffer time before expiration to trigger refresh (1 minute in ms) */
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const setAuthStoreState = (props: SetAuthStoreTokensProps) => {
        set(mapToAuthStoreState(props));
      };

      const clearAuthStoreState = () => {
        set({
          token: null,
          loginFromNewAuthProvider: null,
          isOktaLoginEnabled: false,
          accessTokenExpiresAt: null,
        });
      };

      const logout = async () => {
        const { isOktaLoginEnabled } = get();

        // If user is logged in with Okta, perform clean logout
        // IMPORTANT: We must NOT call clearAuthStoreState() before redirect
        // because it would trigger a React re-render and cause AppRoutes to
        // render <Navigate to="/login" /> before our redirect executes
        if (isOktaLoginEnabled) {
          try {
            const { oktaAuthService } = await import("src/services/oktaAuth.service");

            // Clear Okta tokens and close session on server (await so redirect happens after)
            await oktaAuthService.clearTokens();

            // Clear cookie storage directly (doesn't trigger React state update)
            // This avoids the race condition with React Router's Navigate
            cookieStorage.removeItem("auth-storage");

            // Clear query cache and Datadog user
            queryClient.clear();
            datadogLogs.clearUser();

            // Redirect immediately - page navigates before React can re-render
            window.location.assign(`${window.location.origin}/login`);
            return;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new Error(`Okta signOut error: ${errorMessage}`);
          }
        }

        clearAuthStoreState();
        queryClient.clear();
        datadogLogs.clearUser();
      };

      /**
       * Check if the access token is expired
       * Returns true if token is expired or if no expiration is set
       */
      const isAccessTokenExpired = (): boolean => {
        const { accessTokenExpiresAt } = get();
        if (!accessTokenExpiresAt) {return true;}
        // expiresAt is in seconds, Date.now() is in ms
        return Date.now() >= accessTokenExpiresAt * 1000;
      };

      /**
       * Check if the access token should be refreshed
       * Returns true if token is expired or will expire within the buffer time
       */
      const shouldRefreshAccessToken = (): boolean => {
        const { accessTokenExpiresAt, isOktaLoginEnabled } = get();
        // Only check for OKTA users
        if (!isOktaLoginEnabled) {return false;}
        if (!accessTokenExpiresAt) {return true;}
        // Refresh if within buffer time of expiration
        return Date.now() >= accessTokenExpiresAt * 1000 - TOKEN_REFRESH_BUFFER_MS;
      };

      return {
        token: null,
        loginFromNewAuthProvider: null,
        isOktaLoginEnabled: false,
        accessTokenExpiresAt: null,
        setAuthStoreState,
        logout,
        isAccessTokenExpired,
        shouldRefreshAccessToken,
      };
    },
    {
      name: "auth-storage",
      storage: createJSONStorage(() => cookieStorage),
    }
  )
);

const mapToAuthStoreState = ({
  accessToken,
  isOktaLoginEnabled = false,
  expiresAt,
}: SetAuthStoreTokensProps) => ({
  token: accessToken,
  loginFromNewAuthProvider:
    isOktaLoginEnabled && accessToken
      ? getMyLoginFromAccessToken(accessToken, oktaAuthTokenDecoder)
      : null,
  isOktaLoginEnabled,
  accessTokenExpiresAt: expiresAt ?? null,
});

type AuthState = {
  token: string | null;
  loginFromNewAuthProvider: string | null;
  isOktaLoginEnabled: boolean;
  accessTokenExpiresAt: number | null;
  setAuthStoreState: (props: SetAuthStoreTokensProps) => void;
  logout: () => Promise<void>;
  isAccessTokenExpired: () => boolean;
  shouldRefreshAccessToken: () => boolean;
};

type SetAuthStoreTokensProps = {
  accessToken: string;
  idToken?: string;
  isOktaLoginEnabled?: boolean;
  expiresAt?: number; // Unix timestamp in seconds from OKTA
};
